import React from 'react'
import {
  CompanyMemberRole,
  useInviteCompanyMember,
  AllUsers_users_users,
  useSubperson,
  Subpersons_subperson,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useToaster } from 'utils/useToaster'
import { useCompanyForm } from 'utils/form/companyForm'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { FindOrInviteUser } from 'sharedComponents/FindOrInviteUser'
import Button from 'components/Button'
import Card from 'components/Card'
import Datalist from 'components/Datalist'
import { Dialog } from 'components/Dialog'
import { SelectInput } from 'components/SelectInput'
import { Dropdown } from 'components/AutoCompleteDropdown'
import Icon from 'components/Icon/Icon'

interface AddNewCompanyMembersProps {
  companyId: string
  onRefetchMembers: () => void
}

const AddNewCompanyMembers: React.VFC<AddNewCompanyMembersProps> = ({
  companyId,
  onRefetchMembers,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [isOpenNewUserDialog, setIsOpenNewUserDialog] = React.useState(false)
  const [addCompanyMember, { loading: isAddCompanyMemberLoading }] = useInviteCompanyMember()
  const defaultUser = {
    id: '',
    firstName: null,
    lastName: null,
    email: null,
    parentId: null,
  }
  const [selectedUser, setSelectedUser] = React.useState<Omit<AllUsers_users_users, '__typename'>>(
    defaultUser
  )
  const { data: subperson, loading: isSubpersonLoading } = useSubperson({
    userId: selectedUser?.id,
    companyId,
  })

  const handleOpenNewUserDialog = React.useCallback(() => {
    setIsOpenNewUserDialog(true)
  }, [])

  const handleCloseNewUserDialog = React.useCallback(() => {
    setIsOpenNewUserDialog(false)
    setSelectedUser(defaultUser)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { setToastMessage } = useToaster()

  const companyForm = useCompanyForm({
    validationSchema: 'membersOnly',
    onSubmit: async (values, formikHelpers) => {
      try {
        const promises = values.Members.map(({ User, role }) =>
          addCompanyMember({
            variables: {
              inputData: {
                companyId,
                role,
                userId: User.id!,
              },
            },
          })
        )
        await Promise.all(promises)
        formikHelpers.resetForm()
        handleCloseNewUserDialog()
        onRefetchMembers()
        setSelectedUser(defaultUser)
        setToastMessage('Members successfully added', 'success')
      } catch {
        // ignore
      }
    },
  })

  const handleSelectMemberRole = React.useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>, id?: string) => {
      const { value } = event.target
      void companyForm.setFieldValue(
        'Members',
        companyForm.values.Members.map((companyMember) =>
          companyMember.User.id === id
            ? { ...companyMember, role: value as CompanyMemberRole }
            : companyMember
        )
      )
    },
    [companyForm]
  )

  const handleAddMember = React.useCallback(
    (user: Omit<AllUsers_users_users, '__typename'>) => {
      void companyForm.setFieldValue('Members', [
        ...companyForm.values.Members.filter((companyMember) => companyMember.User.id !== user.id),
        {
          User: user,
          role: CompanyMemberRole.Employee,
          subperson: !!user.parentId,
        },
      ])
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(companyForm.values.Members)]
  )

  React.useEffect(() => {
    if (
      subperson !== undefined &&
      !subperson?.subperson?.length &&
      selectedUser.id !== defaultUser.id
    ) {
      handleAddMember(selectedUser)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subperson])

  const handleDeleteMember = React.useCallback(
    (id: string) => {
      void companyForm.setFieldValue(
        'Members',
        companyForm.values.Members.filter((companyMember) => companyMember.User.id !== id)
      )
    },
    [companyForm]
  )

  return (
    <>
      <Button marginLeft="auto" onClick={handleOpenNewUserDialog} leftIcon={<Icon.Plus size="L" />}>
        Add new user
      </Button>
      <Dialog
        variant={isDesktop ? 'right' : 'fullscreen'}
        maxWidth={isDesktop ? '550px' : '100%'}
        isOpen={isOpenNewUserDialog}
        onDismiss={handleCloseNewUserDialog}
      >
        <Card.Title onDismiss={handleCloseNewUserDialog}>Add company Members</Card.Title>
        <Card.Content>
          <FindOrInviteUser onUserSelect={setSelectedUser} />
          {subperson?.subperson && subperson.subperson.length > 0 && (
            <Dropdown
              name="user"
              value=""
              isLoading={isSubpersonLoading}
              options={[
                {
                  value: selectedUser.id,
                  text: selectedUser.email,
                },
                ...subperson?.subperson?.map((e: Subpersons_subperson | null) => ({
                  value: e?.id || 'No value',
                  text: e?.firstName,
                })),
              ]}
              label="Select main user or subperson"
              isWithOptionsFilter={false}
              selectOnNavigation={false}
              onSelectChange={(_: React.SyntheticEvent<HTMLElement>, data: { value: string }) => {
                const user =
                  data.value === selectedUser.id
                    ? selectedUser
                    : subperson
                        .subperson!.filter(
                          (sub: Subpersons_subperson | null) => sub?.id === data.value
                        )
                        .map((sub: Subpersons_subperson | null) => ({
                          email: selectedUser.email,
                          firstName: sub?.firstName as string,
                          lastName: sub?.lastName as string,
                          id: sub?.id as string,
                          parentId: sub?.parentId as string,
                        }))[0]

                handleAddMember(user)
              }}
            />
          )}
        </Card.Content>
        {companyForm.values.Members.length > 0 && (
          <Datalist
            header={<Datalist.Title>Members</Datalist.Title>}
            footer={
              <Button
                onClick={() => {
                  companyForm.handleSubmit()
                }}
                isFetching={isAddCompanyMemberLoading}
              >
                Add members
              </Button>
            }
            data={companyForm.values.Members}
            renderTableHeader={() => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(2, 1fr) 2.5rem">
                <Datalist.HeaderCell label="Email" />
                <Datalist.HeaderCell label="Role" />
                <Datalist.HeaderCell label="Actions" />
              </Datalist.Row>
            )}
            shouldRenderTableHeader={isDesktop}
            renderTableRow={(member) => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(2, 1fr) 2.5rem">
                <Datalist.Cell label="Email">
                  {!member.subperson
                    ? member.User.email
                    : `${member.User.firstName ?? ''} (${member.User.email ?? ''})`}
                </Datalist.Cell>
                <Datalist.Cell label="Role">
                  <SelectInput
                    value={member.role}
                    disabled={member.subperson}
                    name="memberRole"
                    size="small"
                    onChange={(event) => handleSelectMemberRole(event, member.User.id)}
                    options={[
                      {
                        label: 'Employee',
                        value: CompanyMemberRole.Employee,
                      },
                      {
                        label: 'Owner',
                        value: CompanyMemberRole.Owner,
                      },
                      {
                        label: 'Manager',
                        value: CompanyMemberRole.Manager,
                      },
                    ]}
                    hasBottomContent={false}
                    hasTopContent={false}
                  />
                </Datalist.Cell>
                <Datalist.Cell label="Actions">
                  <Button
                    onClick={() => handleDeleteMember(member.User.id!)}
                    appearance="ghost"
                    colorVariant="danger"
                    size="small"
                  >
                    <Icon.Trash />
                  </Button>
                </Datalist.Cell>
              </Datalist.Row>
            )}
          />
        )}
      </Dialog>
    </>
  )
}

export { AddNewCompanyMembers }
